import {ACTUAL_HOURS_MAX} from '../constants'

/**
 * Phần tính toán thuần của màn Giờ làm thực tế (FAC-26417 FR-001).
 *
 * Tách khỏi component vì hai lý do, không phải vì gọn:
 *
 * 1. Trong repo này **không file test nào import một component nào** — mọi logic nằm trong `.js` của
 *    màn hình đều có mutation score 0 theo cấu tạo. Bài học đã trả giá ở `outputCaption`.
 * 2. Ô nhập giờ là chỗ ranh giới "rỗng ≠ 0" quyết định đúng/sai của cả báo cáo hiệu suất. Nó phải
 *    kiểm được bằng bảng ca, không phải bằng cách gõ thử trên màn hình.
 */

/** Khoá ô trên lưới. Một chỗ sinh ra, để `diffEntries` và màn hình không tự ghép hai kiểu. */
export const cellKey = (actor, day) => `${actor}|${day}`

/**
 * Chuỗi/số người dùng gõ → số giờ sẽ gửi lên service.
 *
 * Trả `{ok, value, error}`. `value === null` nghĩa là **XOÁ ô** (service nhận `hours: null` và gọi
 * `deleteOne`), KHÔNG phải 0 giờ.
 *
 * ⚠️ Đây là ranh giới nguy hiểm nhất của cả màn: `Number('')` và `Number(null)` đều ra **0**, nên
 *    một lần ép số thiếu cẩn thận sẽ biến "chưa nhập" thành "đã khai: hôm đó không làm". Hai thứ đó
 *    đi hai đường hoàn toàn khác nhau ở báo cáo — ngày CHƯA NHẬP bị loại khỏi hiệu suất (BR-2.2),
 *    còn ngày khai 0 giờ thì nằm trong mẫu số. Ép nhầm là hiệu suất của người đó tụt mà không ai
 *    tìm ra tại sao. Đúng cái bẫy đã sập một lần ở FAC-25605 với chỉ số định mức bằng 0.
 *
 * ⚠️ Nhận cả dấu PHẨY thập phân. Người nhập ở xưởng gõ `7,5` — toàn bộ màn hình hiển thị số theo
 *    `vi-VN` (dấu phẩy) nên bắt họ gõ dấu chấm là tự mâu thuẫn. `Number('7,5')` ra `NaN`, tức nếu
 *    không đổi ở đây thì mỗi lần gõ đúng thói quen là một lỗi đỏ.
 */
export const parseHoursInput = (raw) => {
    if (raw === null || raw === undefined) return {ok: true, value: null}

    const text = String(raw).trim().replace(',', '.')

    if (text === '') return {ok: true, value: null}

    const value = Number(text)

    if (!Number.isFinite(value)) return {ok: false, value: null, error: 'Không phải số'}

    if (value < 0) return {ok: false, value: null, error: 'Giờ không âm'}

    if (value > ACTUAL_HOURS_MAX) return {ok: false, value: null, error: `Tối đa ${ACTUAL_HOURS_MAX} giờ`}

    /*
     * Chỉ còn đòi số PHÚT NGUYÊN, không còn đòi bội 0,5.
     *
     * ⚠️ Nới theo đúng `putHours` phía service (nới có chủ đích so với BR-1.2): bảng chấm công của
     *    HR có ô lẻ tới phần tư giờ (`7.75`, `2.25`, `6.75`), nên lưới này phải sửa lại được những
     *    con số đó. Giữ bước 0,5 ở đây là ô hiện `7,75` mà bấm Lưu thì báo sai bước — người dùng
     *    không có cách nào gỡ ngoài việc gõ đè một con số khác do HR không chốt.
     *
     * Gạt sai số dấu phẩy động trước khi so: `10.8 * 60` không ra số nguyên tuyệt đối.
     */
    const minutes = value * 60

    if (Math.abs(minutes - Math.round(minutes)) > 1e-6) {
        return {ok: false, value: null, error: 'Giờ phải tròn đến phút'}
    }

    return {ok: true, value}
}

/**
 * Những ô THỰC SỰ đổi so với dữ liệu server, dạng payload của `PUT control/output/actual-hours`.
 *
 * `edits` chứa mọi ô người dùng đã chạm, kể cả ô đã gõ rồi xoá về đúng giá trị cũ. Gửi hết là ba
 * cái hại cùng lúc: payload phình, nhật ký kiểm toán đầy dòng "sửa" không đổi gì, và — nặng nhất —
 * service đếm chúng vào `overwrites` nên màn hình đòi lý do thay đổi cho một lượt không thay đổi gì.
 */
export const diffEntries = (baseline, edits) => Object.keys(edits || {})
    .filter((key) => {
        const before = baseline[key]
        const after = edits[key]

        // `null` và `undefined` đều nghĩa là "ô trống" nhưng `===` phân biệt chúng; chuẩn hoá trước
        // khi so, nếu không mọi ô trống bị gõ vào rồi xoá lại đều bị tính là một thay đổi.
        return (before === undefined ? null : before) !== (after === undefined ? null : after)
    })
    .map((key) => {
        const separator = key.lastIndexOf('|')

        return {actor: key.slice(0, separator), workDate: key.slice(separator + 1), hours: edits[key]}
    })

/**
 * Số ô bị GHI ĐÈ trong lượt lưu — quyết định có phải hỏi lý do thay đổi hay không (BR-1.4).
 *
 * ⚠️ Ô bị XOÁ cũng tính là ghi đè. Service lọc `overwrites` theo "ô đã có document", không theo giá
 *    trị mới, nên xoá một ô đã lưu mà không khai lý do là ăn 400. Bỏ sót chỗ này thì nút Lưu trông
 *    như bị hỏng: bấm xong hiện lỗi, mà màn hình chưa từng hỏi gì.
 */
export const overwriteCountOf = (baseline, entries) => entries
    .filter((entry) => {
        const before = baseline[cellKey(entry.actor, entry.workDate)]

        return before !== null && before !== undefined
    })
    .length

/** Lưới server → bảng phẳng `actor|ngày → số giờ`, dùng làm mốc so sánh cho mọi phép diff. */
export const baselineOf = (actors) => (actors || []).reduce((acc, row) => {
    Object.keys(row.cells || {}).forEach((day) => {
        acc[cellKey(row.actor, day)] = row.cells[day].hours
    })

    return acc
}, {})

/**
 * Tổng giờ của một dòng SAU khi tính các ô đang sửa dở.
 *
 * ⚠️ Không dùng `row.total` của service khi lưới đang có ô sửa dở: đó là tổng của dữ liệu ĐÃ LƯU.
 *    Người dùng gõ 8 giờ vào ba ô trống mà cột Tổng đứng im thì họ sẽ tưởng mình gõ hụt và gõ lại.
 */
export const rowTotalOf = (row, days, edits) => days.reduce((sum, day) => {
    const key = cellKey(row.actor, day)
    const hours = Object.prototype.hasOwnProperty.call(edits, key) ? edits[key] : (row.cells[day] || {}).hours

    return hours === null || hours === undefined ? sum : sum + hours
}, 0)

/**
 * Số ô CÓ SẢN LƯỢNG MÀ CHƯA CÓ GIỜ, tính cả phần người dùng vừa gõ nhưng chưa lưu.
 *
 * Service trả `missingCount` của dữ liệu đã lưu; dùng thẳng con số đó thì nhập xong cả tuần mà dải
 * cảnh báo vẫn đứng nguyên "còn 42 ô thiếu giờ" cho tới khi bấm Lưu — người dùng sẽ tưởng mình gõ
 * không ăn.
 */
export const missingCountOf = (actors, days, edits) => (actors || []).reduce((count, row) => (
    count + days.filter((day) => {
        const cell = row.cells[day] || {}
        const key = cellKey(row.actor, day)
        const hours = Object.prototype.hasOwnProperty.call(edits, key) ? edits[key] : cell.hours

        return cell.status !== 'future' && cell.output > 0 && (hours === null || hours === undefined)
    }).length
), 0)

/**
 * Lượt điều hướng này có được phép đi qua `<Prompt>` mà KHÔNG hỏi lại không?
 *
 * ⚠️ Vị từ chỉ có hai chữ nhưng đã trượt HAI lần, nên nó đứng riêng ở đây để test được:
 *
 *   · lần một: cờ hạ bằng `setTimeout(0)` trong khi `history.replace` của `setFilter` chạy ở
 *     passive effect — hai hàng đợi khác nguồn, thứ tự không ai bảo đảm;
 *   · lần hai: cờ bật VÔ ĐIỀU KIỆN, kể cả khi chưa dirty. Lúc chưa dirty thì
 *     `<Prompt when={dirty}>` không gắn `history.block`, nên chỗ duy nhất hạ cờ không bao giờ
 *     chạy và cờ kẹt `true`. Người dùng đổi tuần lúc lưới còn sạch, gõ giờ cả tuần, bấm menu —
 *     và rời màn KHÔNG HỎI, mất sạch. Tệ hơn lúc chưa có `Prompt`, vì họ đã tin là có hộp hỏi.
 *
 * Hai điều kiện, cả hai đều BẮT BUỘC: đã thật sự hỏi (`dirty`), và lượt này thật sự điều hướng.
 * Mở modal nhập file không điều hướng — bật cờ ở đó là để nó nằm chờ lượt rời màn kế tiếp.
 */
export const shouldBypassPrompt = ({dirty, navigates}) => Boolean(dirty) && Boolean(navigates)
